import { useApp } from "@/contexts/AppContext";
import Topbar from "@/components/layout/Topbar";
import RiskBadge from "@/components/RiskBadge";
import { groupSum } from "@/lib/forecast";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const fmt = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString() : "0");

// Hoisted chart config — see Dashboard.jsx for the rationale.
const CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: 0 };
const AXIS_TICK = { fontSize: 10, fill: "#64748B" };
const AXIS_LINE = { stroke: "#E2E8F0" };
const TOOLTIP_CURSOR = { fill: "#F8FAFC" };
const BAR_RADIUS_TOP = [3, 3, 0, 0];
const GRID_DASH = "2 2";
const GRID_STROKE = "#F1F5F9";

export default function InventoryRisk() {
  const { forecast, settings } = useApp();
  const rows = forecast.rows;
  const cur = settings.currency;

  const stockoutItems = rows.filter((r) => r.StockoutRisk === "High" || r.StockoutRisk === "Medium")
    .sort((a, b) => (a.StockoutRisk === "High" ? -1 : 1) - (b.StockoutRisk === "High" ? -1 : 1));

  const overstockItems = rows.filter((r) => r.OverstockQty > 0)
    .sort((a, b) => b.OverstockValue - a.OverstockValue);

  const slowItems = rows.filter((r) => r.SlowMoving)
    .sort((a, b) => b.InventoryValue - a.InventoryValue);

  const stockoutByCategory = groupSum(
    rows.filter((r) => r.StockoutRisk !== "Low"),
    "Category",
    "ReorderValue"
  );
  const overstockByCategory = groupSum(
    rows.filter((r) => r.OverstockQty > 0),
    "Category",
    "OverstockValue"
  );
  const slowValueBySite = groupSum(
    rows.filter((r) => r.SlowMoving),
    "Site",
    "InventoryValue"
  );

  return (
    <>
      <Topbar title="Inventory Risk" subtitle="Stockout, overstock, and slow-moving analysis." />
      <div className="flex-1 px-8 py-7 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <ChartCard title={`Stockout Reorder Value by Category`} data={stockoutByCategory} color="#EF4444" cur={cur} testid="chart-stockout-category" />
          <ChartCard title={`Overstock Value by Category`} data={overstockByCategory} color="#0EA5E9" cur={cur} testid="chart-overstock-category" />
          <ChartCard title={`Slow-Moving Value by Site`} data={slowValueBySite} color="#64748B" cur={cur} testid="chart-slow-site" />
        </div>

        <Section title="Stockout Risk Items" count={stockoutItems.length} testid="stockout-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>SKU</th><th>Item</th><th>Site</th>
                <th className="num">Current</th><th className="num">Lead-Time Demand</th>
                <th className="num">Reorder Point</th><th className="num">Recommended Qty</th>
                <th>Supplier</th><th>Risk</th>
              </tr>
            </thead>
            <tbody>
              {stockoutItems.map((r) => (
                <tr key={r.SKU + r.Site}>
                  <td className="font-mono text-slate-900">{r.SKU}</td>
                  <td className="text-slate-700">{r.ItemName}</td>
                  <td className="text-slate-500">{r.Site}</td>
                  <td className="num">{fmt(r.CurrentStock)}</td>
                  <td className="num">{fmt(r.LeadTimeDemand)}</td>
                  <td className="num">{fmt(r.ReorderPoint)}</td>
                  <td className="num font-semibold">{fmt(r.RecommendedOrderQty)}</td>
                  <td className="text-slate-500">{r.SupplierName}</td>
                  <td><RiskBadge level={r.StockoutRisk} /></td>
                </tr>
              ))}
              {!stockoutItems.length && <tr><td colSpan={9} className="text-center text-slate-500 py-8">No stockout risk items.</td></tr>}
            </tbody>
          </table>
        </Section>

        <Section title="Overstock Analysis" count={overstockItems.length} testid="overstock-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>SKU</th><th>Item</th><th>Site</th>
                <th className="num">Current</th><th className="num">Max</th>
                <th className="num">Overstock Qty</th><th className="num">Overstock Value</th>
              </tr>
            </thead>
            <tbody>
              {overstockItems.map((r) => (
                <tr key={r.SKU + r.Site}>
                  <td className="font-mono text-slate-900">{r.SKU}</td>
                  <td className="text-slate-700">{r.ItemName}</td>
                  <td className="text-slate-500">{r.Site}</td>
                  <td className="num">{fmt(r.CurrentStock)}</td>
                  <td className="num">{fmt(r.MaxStock)}</td>
                  <td className="num">{fmt(r.OverstockQty)}</td>
                  <td className="num">{cur}{fmt(r.OverstockValue)}</td>
                </tr>
              ))}
              {!overstockItems.length && <tr><td colSpan={7} className="text-center text-slate-500 py-8">No overstock items.</td></tr>}
            </tbody>
          </table>
        </Section>

        <Section title={`Slow Moving Items (${settings.slowMovingDays}+ days)`} count={slowItems.length} testid="slow-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>SKU</th><th>Item</th><th>Site</th>
                <th className="num">Current</th><th className="num">Avg Monthly Usage</th>
                <th className="num">Unit Cost</th><th className="num">Inventory Value</th>
              </tr>
            </thead>
            <tbody>
              {slowItems.map((r) => (
                <tr key={r.SKU + r.Site}>
                  <td className="font-mono text-slate-900">{r.SKU}</td>
                  <td className="text-slate-700">{r.ItemName}</td>
                  <td className="text-slate-500">{r.Site}</td>
                  <td className="num">{fmt(r.CurrentStock)}</td>
                  <td className="num">{fmt(r.AverageMonthlyUsage)}</td>
                  <td className="num">{cur}{Number(r.UnitCost || 0).toFixed(2)}</td>
                  <td className="num">{cur}{fmt(r.InventoryValue)}</td>
                </tr>
              ))}
              {!slowItems.length && <tr><td colSpan={7} className="text-center text-slate-500 py-8">No slow-moving items detected.</td></tr>}
            </tbody>
          </table>
        </Section>
      </div>
    </>
  );
}

function Section({ title, count, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid={testid}>
      <div className="px-5 py-4 border-b border-slate-200 flex items-baseline justify-between">
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
        <span className="text-xs text-slate-500 font-mono">{count} items</span>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function ChartCard({ title, data, color, cur, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
      <h3 className="font-heading font-semibold text-slate-900 mb-4">{title}</h3>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray={GRID_DASH} stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="name" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
            <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={(v) => `${cur}${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v) => `${cur}${Number(v).toLocaleString()}`} cursor={TOOLTIP_CURSOR} />
            <Bar dataKey="value" fill={color} radius={BAR_RADIUS_TOP} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
